import type {
  ChangeEvent,
  ChangeEventHandler,
  ComponentProps,
  FocusEvent,
  PointerEvent,
} from 'react';
import { useCallback, useId, useRef } from 'react';

import { Icon } from '#/components/Icon/Icon';
import { InputClearButtonSuffix } from '#/components/Input/affixes/InputClearButtonSuffix';
import { Input } from '#/components/Input/Input';
import type { InputProps } from '#/components/Input/InputProps';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ClickOutside } from '#/utils/ClickOutside';

import {
  AutocompleteHolder,
  AutocompleteSuggestion,
  AutocompleteSuggestions,
  AutocompleteSuggestionsHolder,
  AutocompleteTitle,
} from './Autocomplete.styled';
import { useAutocomplete } from './useAutocomplete';

type Props = {
  inputProps: InputProps;
  onChange: ChangeEventHandler;
  onClear?: () => void;
  onSuggestionSelected?: (suggestion: string) => void;
  openOnFocus: boolean;
  presentation: ComponentProps<typeof Input>['styleVariant'];
  qaId?: string;
  shouldShowSuggestions: (inputValue: string) => boolean;
  suggestions: string[];
  title: string;
};

export function Autocomplete({
  inputProps,
  onChange,
  onClear,
  onSuggestionSelected,
  openOnFocus = false,
  presentation,
  qaId,
  shouldShowSuggestions,
  suggestions,
  title,
}: Props) {
  const { i18n } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const generatedInputId = useId();
  const { defaultValue, maxLength, name, placeholder } = inputProps;
  const inputId = inputProps.id ?? generatedInputId;
  const suggestionsContainerId = `${inputId}-autocomplete-listbox`;
  const suggestionsTitleId = `${inputId}-autocomplete-title`;

  const {
    activeIndex,
    closeSuggestions,
    showSuggestions,
    suggestionsRef,
    handleInputBlur,
    handleInputKeyDown,
    handleInputChange,
    handleInputFocus,
    handleSuggestionClick,
  } = useAutocomplete(
    inputRef,
    openOnFocus,
    shouldShowSuggestions,
    suggestions,
    onSuggestionSelected,
  );

  const hasSuggestions = showSuggestions && suggestions.length > 0;
  const activeSuggestionId =
    hasSuggestions && activeIndex !== null
      ? `${suggestionsContainerId}-option-${activeIndex}`
      : undefined;

  const clearInput = useCallback(() => {
    if (inputRef.current) inputRef.current.value = '';
    onClear?.();
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [onClear]);

  const handleSuggestionPointerDown = (e: PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
  };

  return (
    <ClickOutside onClickOutside={closeSuggestions}>
      <AutocompleteHolder>
        <Input
          key={defaultValue?.toString()}
          inputProps={{
            ref: inputRef,
            id: inputId,
            name,
            placeholder,
            defaultValue,
            qaId,
            maxLength,
            onChange: (e: ChangeEvent<HTMLInputElement>) => {
              handleInputChange(e);
              onChange(e);
            },
            onFocus: (e: FocusEvent<HTMLInputElement>) => {
              handleInputFocus(e);
              inputProps.onFocus?.(e);
            },
            onBlur: (e: FocusEvent<HTMLInputElement>) => {
              handleInputBlur(e);
              inputProps.onBlur?.(e);
            },
            onKeyDown: handleInputKeyDown,
            'aria-activedescendant': activeSuggestionId,
            'aria-expanded': hasSuggestions,
            autoComplete: 'off',
            'aria-controls': hasSuggestions
              ? suggestionsContainerId
              : undefined,
            role: 'combobox',
          }}
          suffix={
            <InputClearButtonSuffix
              ariaLabel="Clear search input"
              onClear={clearInput}
              qaId="search-input-clear"
            />
          }
          hideSuffixWhenEmpty
          styleVariant={presentation}
          affixVariant="transparent"
        />

        {hasSuggestions && (
          <AutocompleteSuggestionsHolder>
            <AutocompleteTitle id={suggestionsTitleId}>
              {title}
            </AutocompleteTitle>
            <AutocompleteSuggestions
              id={suggestionsContainerId}
              data-qa-id="autocomplete-suggestions"
              // oxlint-disable-next-line jsx_a11y/prefer-tag-over-role
              role="listbox"
              aria-labelledby={suggestionsTitleId}
              ref={suggestionsRef}
            >
              {suggestions.map((suggestion: string, index) => (
                <AutocompleteSuggestion
                  id={`${suggestionsContainerId}-option-${index}`}
                  // oxlint-disable-next-line react/no-array-index-key
                  key={index}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={activeIndex === index}
                  aria-label={i18n('Popular Search Term - %(suggestion)s', {
                    suggestion,
                  })}
                  data-qa-id="autocomplete-suggestion"
                  onPointerDown={handleSuggestionPointerDown}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  <Icon name="search" size={20} />
                  {suggestion}
                </AutocompleteSuggestion>
              ))}
            </AutocompleteSuggestions>
          </AutocompleteSuggestionsHolder>
        )}
      </AutocompleteHolder>
    </ClickOutside>
  );
}
